import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApiCall } from '../hooks/useApiCall';
import { RiArticleLine, RiArrowRightLine, RiArrowLeftLine, RiArrowRightSLine, RiCloseLine, RiSearchLine } from 'react-icons/ri';
import '../styles/Blogs.css';

const PAGE_SIZE = 12;

function SkeletonCard() {
  return (
    <div className="blogs-skeleton-card">
      <div className="skeleton-image" />
      <div className="skeleton-body">
        <div className="skeleton-line short" />
        <div className="skeleton-line title long" />
        <div className="skeleton-line medium" />
        <div className="skeleton-line long" />
        <div className="skeleton-line medium" />
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function ProjectCard({ blog }) {
  const tags = blog.tags ? blog.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <Link to={`/projects/${blog.slug}`} className="blog-card">
      {blog.cover_image
        ? <img className="blog-card-image" src={blog.cover_image} alt={blog.title} loading="lazy" />
        : (
          <div className="blog-card-image-placeholder">
            <RiArticleLine />
          </div>
        )
      }
      <div className="blog-card-body">
        {tags.length > 0 && (
          <div className="blog-card-tags">
            {tags.slice(0, 3).map(tag => (
              <span key={tag} className="blog-card-tag">{tag}</span>
            ))}
          </div>
        )}
        <h2 className="blog-card-title">{blog.title}</h2>
        {blog.excerpt && <p className="blog-card-summary">{blog.excerpt}</p>}
        <div className="blog-card-meta">
          {blog.author && <span>{blog.author}</span>}
          {blog.author && blog.created_at && <span className="blog-card-meta-dot" />}
          {blog.created_at && <span>{formatDate(blog.created_at)}</span>}
        </div>
        <span className="blog-card-read-more">Read more <RiArrowRightSLine /></span>
      </div>
    </Link>
  );
}

export default function Projects() {
  const apiCall = useApiCall();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const hasLoadedRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    document.title = 'Projects - Building Depot Suriname';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Explore the latest projects, ideas, and updates from Building Depot Suriname.');
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchTerm.trim());
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    const fetchBlogs = async () => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const showSkeleton = !hasLoadedRef.current;

      setLoading(showSkeleton);
      setRefreshing(!showSkeleton);
      setError(null);
      try {
        const response = await apiCall('blogs/get', 'POST', {
          page,
          limit: PAGE_SIZE,
          query: searchQuery
        });
        if (requestId !== requestIdRef.current) return;

        if (response && response.success && response.data) {
          setBlogs(response.data.blogs || []);
          setTotalPages(response.data.pagination?.total_pages || response.data.total_pages || 1);
          hasLoadedRef.current = true;
        } else {
          setError('Failed to load projects');
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError('Failed to load projects');
        console.error('Error fetching blogs:', err);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    fetchBlogs();
  }, [page, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const showAround = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= page - showAround && i <= page + showAround)
      ) {
        pages.push(i);
      } else if (
        i === page - showAround - 1 ||
        i === page + showAround + 1
      ) {
        pages.push('...');
      }
    }

    // deduplicate consecutive ellipses
    const deduped = pages.filter((v, i) => !(v === '...' && pages[i - 1] === '...'));

    return (
      <div className="blogs-pagination">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(page - 1)}
          disabled={page <= 1}
        >
          <RiArrowLeftLine /> Prev
        </button>

        {deduped.map((p, i) =>
          p === '...'
            ? <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#999' }}>…</span>
            : (
              <button
                key={p}
                className={`pagination-btn${page === p ? ' active' : ''}`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            )
        )}

        <button
          className="pagination-btn"
          onClick={() => handlePageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next <RiArrowRightLine />
        </button>
      </div>
    );
  };

  return (
    <div className="blogs-page">
      <div className="blogs-container">
        <div className="blogs-header">
          <div>
            <h1>Projects</h1>
            <p>Project highlights, ideas, and updates from Building Depot Suriname</p>
          </div>

          <div className="blogs-search">
            <div className="blogs-search-wrapper">
              <RiSearchLine className="blogs-search-icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search projects..."
                className="blogs-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="blogs-search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <RiCloseLine />
                </button>
              )}
            </div>
            {(searchQuery || refreshing) && (
              <span className="blogs-search-count">
                {refreshing
                  ? 'Searching...'
                  : blogs.length === 0 ? `No results for "${searchQuery}"` : `Showing results for "${searchQuery}"`}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="blogs-skeleton-grid">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="blogs-empty">
            <div className="blogs-empty-icon"><RiArticleLine /></div>
            <h3>Something went wrong</h3>
            <p>{error}</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="blogs-empty">
            <div className="blogs-empty-icon"><RiArticleLine /></div>
            <h3>{searchQuery ? 'No projects found' : 'No posts yet'}</h3>
            <p>{searchQuery ? 'Try another search term.' : 'Check back soon for new content.'}</p>
          </div>
        ) : (
          <>
            <div className={`blogs-grid ${refreshing ? 'is-refreshing' : ''}`}>
              {blogs.map(blog => <ProjectCard key={blog.id} blog={blog} />)}
            </div>
            {renderPagination()}
          </>
        )}
      </div>
    </div>
  );
}
